import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const initialOtpState = {
  phone: '',
  otp: '',
  first_name: '',
  last_name: '',
  email: '',
};

const normalizeIndianPhone = (value) => value.replace(/\D/g, '').slice(-10);

export default function OtpLoginPage() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useAuth();
  const [form, setForm] = useState(initialOtpState);
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [sessionMeta, setSessionMeta] = useState({
    otp_session_id: '',
    masked_phone: '',
    expires_in: 300,
    is_new_user: false,
  });

  const phoneDisplay = useMemo(() => {
    const clean = normalizeIndianPhone(form.phone);
    return clean ? `+91 ${clean}` : '+91';
  }, [form.phone]);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: key === 'phone' ? normalizeIndianPhone(value) : value,
    }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const phone = normalizeIndianPhone(form.phone);
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const res = await sendOtp(`+91${phone}`);
      setSessionMeta({
        otp_session_id: res.data?.otp_session_id || res.otp_session_id || '',
        masked_phone: res.data?.masked_phone || res.masked_phone || `+91 ${phone}`,
        expires_in: res.data?.expires_in || res.expires_in || 300,
        is_new_user: Boolean(res.data?.is_new_user ?? res.is_new_user),
      });
      setStep('otp');
      toast.success('OTP sent successfully');
    } catch (err) {
      toast.error(err.message || 'Unable to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const phone = normalizeIndianPhone(form.phone);
    if (form.otp.trim().length < 4) {
      toast.error('Please enter the OTP');
      return;
    }

    setLoading(true);
    try {
      await verifyOtp({
        phone: `+91${phone}`,
        otp: form.otp.trim(),
        otp_session_id: sessionMeta.otp_session_id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
      });
      toast.success('Login successful! 🥻');
      navigate('/account');
    } catch (err) {
      toast.error(err.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    const phone = normalizeIndianPhone(form.phone);
    if (phone.length !== 10) {
      toast.error('Enter a valid mobile number first');
      return;
    }
    setLoading(true);
    try {
      const res = await sendOtp(`+91${phone}`);
      setSessionMeta((prev) => ({
        ...prev,
        otp_session_id: res.data?.otp_session_id || res.otp_session_id || prev.otp_session_id,
        masked_phone: res.data?.masked_phone || res.masked_phone || prev.masked_phone,
        expires_in: res.data?.expires_in || res.expires_in || prev.expires_in,
        is_new_user: Boolean(res.data?.is_new_user ?? res.is_new_user ?? prev.is_new_user),
      }));
      toast.success('OTP resent');
    } catch (err) {
      toast.error(err.message || 'Could not resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth-shell">
      <div className="auth-card auth-card-wide">
        <div className="auth-brand-block">
          <div className="auth-icon">📱</div>
          <h1 className="auth-title">Login with Mobile OTP</h1>
          <p className="auth-subtitle">
            Fast checkout, order tracking, saved addresses, and WhatsApp-friendly login for your customers.
          </p>
        </div>

        <div className="auth-toggle-row">
          <Link to="/login" className="auth-toggle-link">Email Login</Link>
          <Link to="/register" className="auth-toggle-link auth-toggle-link-muted">Create Account</Link>
        </div>

        {step === 'phone' ? (
          <form onSubmit={handleSendOtp}>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              <div className="phone-input-wrap">
                <span className="phone-prefix">+91</span>
                <input
                  className="form-input phone-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="9032672270"
                  required
                />
              </div>
            </div>

            <div className="otp-note-card">
              <div>✅ Password-free login</div>
              <div>✅ Better conversion for WhatsApp shoppers</div>
              <div>✅ Faster repeat orders and address saving</div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '14px', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <div className="otp-status-banner">
              <div>
                OTP sent to <strong>{sessionMeta.masked_phone || phoneDisplay}</strong>
              </div>
              <button type="button" onClick={() => setStep('phone')} className="otp-link-btn">
                Change number
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Enter OTP</label>
              <input
                className="form-input otp-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={form.otp}
                onChange={(e) => updateField('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit OTP"
                required
              />
            </div>

            {sessionMeta.is_new_user && (
              <div className="otp-profile-grid">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    className="form-input"
                    value={form.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    className="form-input"
                    value={form.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    placeholder="Last name"
                  />
                </div>
                <div className="form-group otp-profile-email">
                  <label className="form-label">Email (optional)</label>
                  <input
                    className="form-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>
              </div>
            )}

            <div className="otp-helper-row">
              <span>OTP valid for about {Math.ceil((sessionMeta.expires_in || 300) / 60)} minutes</span>
              <button type="button" className="otp-link-btn" onClick={resendOtp} disabled={loading}>
                Resend OTP
              </button>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '14px', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>
          </form>
        )}

        <div className="auth-footer-text">
          By continuing, customer login can work with OTP first and email/password as backup.
        </div>
      </div>
    </div>
  );
}
